// Libs
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Components
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Box from '@material-ui/core/Box';

// Actions
import { selectVideoSource } from 'REDUCERS/cameraSlice';

// Utils
import useTranslator from 'UTILS/hooks/useTranslator';
import useFocus from 'UTILS/hooks/useFocus';
import { CAMERA_CHECK, DONE, RUNNING } from 'UTILS/constants';
import { stopStream } from 'UTILS/stream';
import translations from './locales.json';

const CameraDiagnostic = () => {
  const dispatch = useDispatch();
  const cameraCheck = useSelector(state => state.checks[CAMERA_CHECK]);
  const currentCheck = useSelector(state => state.nav.currentCheck);
  const videoEl = useRef();
  const [stream, setStream] = useState(null);
  const [translate] = useTranslator(translations);

  // Show vanity video only if current check is the cam one, and only if it's done
  useEffect(() => {
    let shouldAbort = false;
    if (currentCheck === CAMERA_CHECK && cameraCheck.stage === DONE) {
      navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: cameraCheck.selectedVideoSourceId,
        },
      }).then((newStream) => {
        if (!shouldAbort) {
          setStream(newStream);
          if (videoEl.current) {
            videoEl.current.srcObject = newStream;
          }
        } else {
          stopStream(newStream);
        }
      });
    } else {
      stopStream(stream);
      setStream(null);
    }
    return () => {
      shouldAbort = true;
    };
  }, [currentCheck, cameraCheck.stage]);

  // Hide vanity video when the tab is blurred for performance concern
  let shouldAbort = false;
  useFocus(() => {
    if (currentCheck === CAMERA_CHECK && cameraCheck.stage === DONE) {
      shouldAbort = false;
      navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: cameraCheck.selectedVideoSourceId,
        },
      }).then((newStream) => {
        if (!shouldAbort) {
          setStream(newStream);
          if (videoEl.current) {
            videoEl.current.srcObject = newStream;
          }
        } else {
          stopStream(newStream);
        }
      });
    }
  }, () => {
    stopStream(stream);
    setStream(null);
    shouldAbort = true;
  }, [stream, currentCheck, cameraCheck.stage]);

  const handleSourceChange = ({ target }) => {
    stopStream(stream);
    setStream(null);
    dispatch(selectVideoSource({ deviceId: target.value }));
  };

  return (
    <Box className='cameraDiagnostic'>
      {cameraCheck.videoSourceList.length > 0 && (
        <Box className='cameraDiagnostic-labelWrapper'>
          <FormControl variant='filled' fullWidth disabled={cameraCheck.stage === RUNNING}>
            <InputLabel id='cameraDiagnostic-selectLabel'>{translate('device')}</InputLabel>
            <Select
              labelId='cameraDiagnostic-selectLabel'
              id='demo-simple-select-filled'
              value={cameraCheck.selectedVideoSourceId}
              onChange={handleSourceChange}
            >
              {cameraCheck.videoSourceList.map(source => (
                <MenuItem key={source.deviceId} value={source.deviceId}>
                  {source.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      {stream && (
        <Box className='cameraDiagnostic-videoWrapper'>
          <video muted autoPlay playsInline ref={videoEl} className='cameraDiagnostic-video' id='cameraDiagnostic-video' />
        </Box>
      )}
    </Box>
  );
};

export default CameraDiagnostic;
